import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import filterDuck from 'components/filter/filter-options/duck'
import Loader from 'components/loader'
import useSearch from 'components/shared/useSWR/useSearch'
import sortDuck from 'components/sort/sort-options/duck'
import {
  SEGMENT_STORAGE_KEY_SEARCH_ID,
  SEGMENT_STORAGE_KEY_SEARCH_IDX,
} from 'lib/segment/const'
import ValidSearchContent from './has-result'
import EmptySearchResult from './no-result'
import SearchRequestError from './request-error'
import styles from './style.scss'
import { checkHasFilterQuickSize, getSearchResultParams } from './utils'

const SearchContent = ({
  filter,
  filterableCategories,
  isPhone,
  query,
  results: initialData,
  sort,
  sortAndFilterData,
  track,
  onChangeURL,
  sortBy,
  updateSort,
}) => {
  const isSearching = useRef(true)
  const [isFetchData, setIsFetchData] = useState(false)
  const [page, setPage] = useState(0)
  const [prevPage, setPrevPage] = useState(undefined)
  const [products, setProducts] = useState(initialData?.products || [])
  const [totalHits, setTotalHits] = useState(initialData?.totalHits)
  const [quickSizes, setQuickSize] = useState(initialData?.quick_sizes)
  const [sortData, setSortData] = useState(sortBy || 'popular')
  const [changeSort, setchangeSort] = useState(false)

  const fetchOptions = getSearchResultParams(filter, page, query, sortData)

  const {
    data: results,
    error,
    retry,
    isValidating,
  } = useSearch(fetchOptions, initialData)

  const invokeTrack = useCallback(
    (newResults) => {
      const {
        products: resProducts,
        totalHits: resTotal,
        queryID: queryId,
        index: query_idx,
      } = newResults

      sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_ID, queryId)
      sessionStorage.setItem(SEGMENT_STORAGE_KEY_SEARCH_IDX, query_idx)

      track({
        hits: resProducts,
        query,
        page: page + 1,
        total: resTotal,
        query_id: queryId,
        query_idx,
      })
    },
    [query, track, page],
  )

  const loadMoreProducts = () => {
    isSearching.current = true
    setPage(page + 1)
  }

  const clearPageAfterFilter = () => {
    setPage(0)
    setPrevPage(undefined)
    window.scroll(0, 0)
  }

  // On component mounted
  useEffect(() => {
    if (initialData) {
      invokeTrack(initialData)
      isSearching.current = false
      setIsFetchData(false)
    }

    updateSort(sortBy)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check: if have change filter / sort should be reset page number / changeURL for refresh page
  useEffect(() => {
    // if filter reset page as page 0
    setIsFetchData(false)
    clearPageAfterFilter()
    setSortData(sort.by || 'popular')
    onChangeURL()
  }, [filter, sort, onChangeURL])

  useEffect(() => {
    updateSort('popular')
    setchangeSort(false)
  }, [query, updateSort])

  // check is change the sort filter
  const onSortFilterChanged = () => {
    setchangeSort(true)
    setPage(0)
    setPrevPage(undefined)
  }

  useEffect(() => {
    if (results?.products && prevPage !== page) {
      invokeTrack(results)
      if (page > 0) {
        setProducts([...products, ...results.products])
        // sometime, after load more product, that found totalHits as undefined
        if (results.totalHits) {
          setTotalHits(results.totalHits)
        }
      } else {
        setProducts([...results.products])
        // sometime, after fetch data, the quick_size is lost that will make the ui error
        // so, will be change only receive quick_sizes data
        if (results.quick_sizes) {
          setQuickSize(results.quick_sizes)
        }
        setTotalHits(results.totalHits)
      }
      isSearching.current = false
      setPrevPage(page)
    }
    setIsFetchData(Boolean(results?.products))
  }, [invokeTrack, page, results, products, prevPage])

  const setShowErrorDefault =
    page === 0 &&
    !products?.length &&
    !changeSort &&
    !checkHasFilterQuickSize(filter, isPhone, quickSizes)

  if (products?.length === 0 && !isFetchData && !error) {
    return (
      <div className="loader-container">
        <style jsx>{styles}</style>
        <Loader />
      </div>
    )
  }

  // for search and not found product at the page 0
  // that will show full width (ui) of no result
  if (isFetchData && setShowErrorDefault && !error) {
    return <EmptySearchResult query={query} />
  }

  const searchContent = (
    <ValidSearchContent
      filterableCategories={filterableCategories}
      isFetchData={isFetchData}
      isSearching={isSearching}
      loadMoreProducts={loadMoreProducts}
      onSortFilterChanged={onSortFilterChanged}
      products={products}
      query={query}
      quickSizes={quickSizes}
      sortAndFilterData={sortAndFilterData}
      totalHits={totalHits}
    />
  )

  if (error) {
    return (
      <React.Fragment>
        <SearchRequestError retry={retry} isValidating={isValidating} />
        {products.length > 0 && searchContent}
      </React.Fragment>
    )
  }

  return searchContent
}

SearchContent.defaultProps = {
  filterableCategories: null,
  query: '',
  results: null,
  sortBy: 'popular',
}

SearchContent.propTypes = {
  filter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  filterableCategories: PropTypes.arrayOf(PropTypes.shape({})),
  isPhone: PropTypes.bool.isRequired,
  query: PropTypes.string,
  results: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({})),
    quick_sizes: PropTypes.arrayOf(PropTypes.string),
    totalHits: PropTypes.number,
  }),
  sort: PropTypes.shape({ by: PropTypes.string }).isRequired,
  sortAndFilterData: PropTypes.shape({}).isRequired,
  track: PropTypes.func.isRequired,
  onChangeURL: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  updateSort: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    filter: state.filter,
    isPhone: state.device.isPhone,
    sort: state.sort,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        cleanFilters: filterDuck.creators.clean,
        cleanSort: sortDuck.creators.clean,
        updateSort: sortDuck.creators.update,
      },
      dispatch,
    ),
)(SearchContent)
